import { useCallback, useEffect, useRef, useState } from "react";
import { StandaloneCouponsAPI } from "../../../providers/StandaloneCouponsAPI";
import { Button } from "../../components/Button";
import { Drawer } from "../../components/Drawer";
import { Loading } from "../../components/Loading";
import { If } from "../../components/control-statements";

import { OlgaEnvironmentsAPI } from "../../../providers/OlgaEnvironmentsAPI";
import { Label } from "../../components/Label";
import { Radio } from "../../components/Radio";
import { BenefitInsertCodeStep } from "../../containers/beneficios/steps/BenefitInsertCodeStep";
import { BenefitSelectTypeStep } from "../../containers/beneficios/steps/BenefitSelectTypeStep";
import { BenefitShowQrCodeStep } from "../../containers/beneficios/steps/BenefitShowQrCodeStep";
import { BenefitUseTypeStep } from "../../containers/beneficios/steps/BenefitUseTypeStep";
import { BenefitEnvironment } from "./steps/BenefitEnvironment";
import BenefitVisibility from "./steps/BenefitVisibility";
import { Environment } from "shared/core/Environment";

import styles from "../../../styles/beneficios/steps/BenefitTypeSelectStep.module.scss";
import { MiniCheck, MiniInfo } from "shared/components/Icons";
import classNames from "classnames/bind";
import { BaseCurrencyInput, Hairline } from "@components";
import { GodMode } from "shared/core/GodMode";
import Toggle2 from "shared/components/Toggle2";
import ContemplateCustomers from "./steps/ContemplateCustomers";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { UserStorage } from "shared/tools/UserStorage";
import { Currency } from "shared/utils/Currency";
import { BenefitUnits } from "./steps/BenefitUnits";
import { EnvironmentsAPI } from "providers/EnvironmentsAPI";
const css = classNames.bind(styles);

dayjs.extend(customParseFormat);

const STEPS = [
  { key: "TYPE", label: "Determine o benefício", show: true },
  { key: "INFOS", label: "Informações básicas", show: true },
  { key: "ADD", label: "Adição à carteira", show: true },
  {
    key: "FIDELITY",
    label: "Fidelidade",
    show: true,
  },
  // {
  //   key: "CONTEMPLATE",
  //   label: "Contemplação",
  //   show: true,
  // },
  {
    key: "AVAILABILITY_USE",
    label: "Disponibilidade e uso",
    show: true,
    items: [
      { key: "USE", label: "Uso", show: true },
      { key: "CHANNELS", label: "Canais", show: true },
      { key: "DAYS", label: "Dias e horários", show: true },
      { key: "BRANDS", label: "Marcas", show: true },
      { key: "UNITS", label: "Unidades", show: true },
      { key: "DISCLOSURE", label: "Divulgação", show: true },
    ],
  },
];

export const BenefitFormDrawer = ({
  visible,
  onClose,
  onSave,
  benefitToEdit = {},
}) => {
  const [loading, setLoading] = useState(false);
  const [showQrCode, setShowQrCode] = useState(false);
  const [benefit, setBenefit] = useState({});
  const [isValid, setIsValid] = useState(false);
  const [brands, setBrands] = useState([]);
  const [units, setUnits] = useState([]);
  const [partners, setPartners] = useState([]);
  const [blockAdd, setBlockAdd] = useState(false);
  const [steps, setSteps] = useState(STEPS);
  const [selectedStep, setSelectedStep] = useState(steps[0].key);
  const [completedSteps, setCompletedSteps] = useState(steps[0].key);

  useEffect(() => {
    if (visible) {
      setCompletedSteps([]);
      setBenefit({ contemplate: false });
    }
  }, [visible]);

  const getBrands = async () => {
    let { environment_id, user_type } = UserStorage.getLoggedUser();

    if (user_type === "BRAND") {
      const holdingResponse = await EnvironmentsAPI.getById(environment_id);
      environment_id = holdingResponse.getData({}).environment_id;
    }

    const response = await EnvironmentsAPI.getUnits(String(environment_id));
    const brands = response.getData({}).brands;

    let brandsList = [];
    let unitsList = [];

    for (const b of brands) {
      const response = await EnvironmentsAPI.getUnits(String(b.id));
      let units = response.getData({})["units"];

      units = units.map((u) => {
        return {
          id: u.id,
          name: u.name,
          brand_id: b.id,
          brand_name: b.name,
        };
      });

      unitsList.push(units);

      brandsList.push({
        id: b.id,
        name: b.name,
      });
    }

    setBrands(brandsList);
    setUnits((unitsList || []).flatMap((b) => b));

    if ((brandsList || []).length < 2) {
      setSteps(
        steps.map((step) => {
          if (step.key === "AVAILABILITY_USE") {
            step.items = step.items.filter((item) => item.key !== "BRANDS");
          }

          return step;
        })
      );
    }
  };

  const getPartners = async () => {
    const response = await OlgaEnvironmentsAPI.getPartners();
    const partners = response.getData([]);
    setPartners(partners);
  };

  const checkTimeErrors = (visibility) => {
    const conflicts = [];

    visibility?.forEach((day) => {
      day.times.forEach((time) => {
        if (time.start === time.end || time.start >= time.end) {
          conflicts.push({
            weekDay: day.weekDay,
            conflictingTime: time,
          });
        }
      });
    });

    return conflicts.length > 0;
  };

  const validateStep = useCallback(
    (benefit) => {
      switch (selectedStep) {
        case "TYPE": {
          return !!benefitTypes[benefit.type];
        }
        case "INFOS": {
          let isValueOk = false;
          if (
            [
              benefitTypes.PERCENTAGE_DISCOUNT,
              benefitTypes.CASHBACK,
              benefitTypes.PRODUCT_DISCOUNT,
            ].includes(benefit.type)
          ) {
            isValueOk =
              Number(benefit.value) > 0 && Number(benefit.value) <= 100;
          } else if (benefit.type === benefitTypes.FREE_DELIVERY) {
            isValueOk = true;
          } else {
            isValueOk = Number(benefit.value) > 0;
          }

          const expirationDate = dayjs(benefit.expiration_date, "DD/MM/YYYY");
          const spendDate = dayjs(benefit.spend_date, "DD/MM/YYYY");

          const isCodeOk = (benefit.code || "").length > 0;
          const isExpirationOk =
            Number(benefit.expiration_days) > 0 ||
            (benefit.expiration_date &&
              expirationDate.isSameOrAfter(dayjs(), "day") &&
              benefit.expiration_date.length === 10);
          const isSpendExpirationOk =
            (Number(benefit.spend_days) >= 0 && !benefit.expiration_date) ||
            !benefit.spend_date ||
            (benefit.spend_date &&
              spendDate.isSameOrAfter(dayjs(), "day") &&
              (!benefit.expiration_date ||
                (spendDate.isSameOrAfter(
                  dayjs(benefit.expiration_date),
                  "day"
                ) &&
                  benefit.expiration_date.length === 10)));

          const isGroupOk =
            Environment.id == 34 ? (benefit.group || "").length > 0 : true;

          const isCommonOk =
            isCodeOk && isExpirationOk && isSpendExpirationOk && isValueOk;

          const hasSkus =
            benefit.skus?.length > 0 || benefit.product_list?.length > 0;
          const hasProductName = (benefit.description || "").length > 0;

          const isCouponProductOk =
            [
              benefitTypes.PRODUCT_DISCOUNT,
              benefitTypes.PRODUCT_CASH_DISCOUNT,
            ].includes(benefit.type) &&
            hasSkus &&
            hasProductName &&
            isCommonOk;

          if (isCouponProductOk) return true;

          const isCouponOk =
            ![
              benefitTypes.CASHBACK,
              benefitTypes.PRODUCT_DISCOUNT,
              benefitTypes.PRODUCT_CASH_DISCOUNT,
            ].includes(benefit.type) && isCommonOk;
          const isCashbackOk =
            benefit.type === benefitTypes.CASHBACK && isCommonOk;
          return (isCouponOk || isCashbackOk) && isGroupOk;
        }
        case "ADD": {
          return (
            benefit.grant_type === "SYSTEM" || benefit.grant_type === "MANUAL"
          );
        }
        case "FIDELITY": {
          return (
            !benefit.sellable ||
            (benefit.sellable &&
              benefit.price !== null &&
              benefit.price !== undefined)
          );
        }
        case "DAYS": {
          if (benefit.visibility?.length === 0) return false;
          const isVisibilityOk = !checkTimeErrors(benefit.visibility);
          return isVisibilityOk;
        }
        case "USE": {
          return (
            benefit.single_use !== null &&
            benefit.single_use !== undefined &&
            (blockAdd === false || (!!blockAdd && benefit.max_adds > 0))
          );
        }
        case "CHANNELS": {
          return benefit.type_use > 0 && benefit.type_use < 16;
        }
        case "BRANDS": {
          return (
            ((brands || []).length > 1 &&
              (benefit.brands_valid || []).length > 0) ||
            brands.length === 1
          );
        }
        case "UNITS": {
          return (
            ((units || []).length > 1 &&
              (benefit.units_valid || []).length > 0) ||
            units.length === 1
          );
        }
        case "CONTEMPLATE": {
          return (
            !benefit.contemplate ||
            (!!benefit.contemplate && benefit.customersPhone?.length > 0)
          );
        }
        case "DISCLOSURE": {
          return true;
        }
        default:
          break;
      }

      return false;
    },
    [brands, units, selectedStep, blockAdd]
  );

  useEffect(() => {
    getBrands();
    getPartners();
  }, []);

  const onChangeBenefit = (b) => {
    setBenefit(b);
    setIsValid(validateStep(b));
  };

  useEffect(() => {
    let filteredSteps = STEPS;

    filteredSteps = STEPS.filter((step) => step.show);

    if (benefitToEdit) {
      filteredSteps = STEPS.filter((step) => step.key !== "TYPE").map(
        (step) => ({
          ...step,
          items: step.items?.filter((item) => item.key !== "CHANNELS"),
        })
      );
    }

    if (benefitToEdit) {
      filteredSteps = STEPS.map((step) => {
        if (step.key === "AVAILABILITY_USE") {
          return {
            ...step,
            items: step.items?.filter((item) => item.key !== "USE"),
          };
        }

        return step;
      });
    }

    setSteps(filteredSteps);
    setSelectedStep(filteredSteps[0].key);
  }, [visible, benefitToEdit]);

  useEffect(() => {
    if (visible) setShowQrCode(false);
  }, [visible]);

  const initialized = useRef(false);

  useEffect(() => {
    if (
      !initialized.current &&
      benefitToEdit &&
      Object.values(benefitToEdit)?.length > 0
    ) {
      if (benefitToEdit?.expiration_date) {
        benefitToEdit.expiration_date = dayjs(
          benefitToEdit.expiration_date
        ).format("DD/MM/YYYY");
      }
      if (benefitToEdit?.spend_date) {
        benefitToEdit.spend_date = dayjs(benefitToEdit.spend_date).format(
          "DD/MM/YYYY"
        );
      }
      setBenefit(benefitToEdit);
      initialized.current = true;
    } else setBenefit({ contemplate: false });
  }, [benefitToEdit]);

  useEffect(() => {
    if (benefit.max_adds) setBlockAdd(benefit.max_adds > 0);
  }, [benefit]);

  const normalizeDate = (dateStr) => {
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      return dateStr;
    }

    const parsed = dayjs(dateStr, "DD/MM/YYYY", true);
    if (parsed.isValid()) {
      return parsed.format("YYYY-MM-DD");
    }

    return dateStr;
  };

  const saveCoupon = async () => {
    setLoading(true);

    let requestBody = {};

    if (benefit.expiration_date) {
      requestBody.expiration_date = normalizeDate(benefit.expiration_date);
    }

    if (benefit.spend_date) {
      requestBody.spend_date = normalizeDate(benefit.spend_date);
    }

    if (benefit.type === "DELIVERY") requestBody.value = 0;

    let response;

    if (benefitToEdit) {
      requestBody = {
        ...requestBody,
        contemplate: false,
        type: benefit?.type,
        code: benefit?.code,
        value: benefit?.value,
        single_use: benefit?.single_use,
        min_order: benefit?.min_order || 0,
        expiration_days: benefit?.expiration_days || null,
        spend_days: benefit?.spend_days || 0,
        multiple_items: benefit?.multiple_items || false,
        skus: benefit?.product_list || [],
        description: benefit?.description,
        image: benefit?.image || null,
        partner_id: benefit?.partner_id || null,
        sellable: benefit?.sellable || false,
        public: benefit?.public || false,
        price: benefit?.price || 0,
        group: benefit?.group || "",
        justification: benefit?.justification || "",
        visibility: benefit?.visibility || null,
        type_use: benefit?.type_use,
        brands_valid: (benefit?.brands_valid || []).map((b) => b.id || b),
        units_valid: (benefit?.units_valid || []).map((u) => u.id || u),
        grant_type: benefit?.grant_type,
        standalone: true,
        status: "ACTIVATED",
        ...(benefit?.max_adds > 0 && {
          max_adds: benefit?.max_adds,
        }),
        ...(benefit?.folder && {
          folder: benefit?.folder,
        }),
        ...(benefit?.template && { template: benefit?.template }),
      };

      response = await StandaloneCouponsAPI.putStandaloneCoupons(
        requestBody,
        benefitToEdit?.id
      );
      setLoading(false);

      const errors = response.getErrors([]);
      if (errors.length > 0) return alert({ text: errors[0] });

      const savedBenefit = response.getData({});

      setBenefit({ ...requestBody, ...benefit });

      if (onSave) onSave(benefitToEdit?.id);
    } else {
      requestBody = {
        ...benefit,
        ...requestBody,
        standalone: true,
        status: "ACTIVATED",
      };

      response = await StandaloneCouponsAPI.postStandaloneCoupons(requestBody);
      setLoading(false);

      const errors = response.getErrors([]);
      if (errors.length > 0) return alert({ text: errors[0] });

      const savedBenefit = response.getData({});

      setBenefit({ ...requestBody, pin: savedBenefit?.pin });

      if (onSave) onSave();
    }

    setShowQrCode(true);
  };

  const getVisibleSteps = () => steps.filter((step) => step.show);

  const nextStep = () => {
    const visibleSteps = getVisibleSteps();

    const mainIndex = visibleSteps.findIndex((s) => s.key === selectedStep);

    const availability = visibleSteps.find((s) => s.key === "AVAILABILITY_USE");
    const items = availability?.items || [];
    const subIndex = items.findIndex((i) => i.key === selectedStep);

    if (subIndex >= 0) {
      const nextItem = items[subIndex + 1];
      if (nextItem) {
        setSelectedStep(nextItem.key);
      } else {
        const after = visibleSteps.find(
          (s) =>
            s.key !== "AVAILABILITY_USE" &&
            visibleSteps.indexOf(s) > visibleSteps.indexOf(availability)
        );

        if (after) setSelectedStep(after.key);
      }
    } else {
      const currentMain =
        mainIndex >= 0 ? mainIndex : visibleSteps.indexOf(availability);
      const nextMain = visibleSteps[currentMain + 1];

      if (!nextMain) return;

      if (nextMain.key === "AVAILABILITY_USE") {
        if (items.length > 0) {
          setSelectedStep(items[0].key);
        }
      } else {
        setSelectedStep(nextMain.key);
      }
    }

    setIsValid(validateStep(benefit));
    setCompletedSteps((prev) => [...prev, selectedStep]);
  };

  useEffect(() => {
    setIsValid(validateStep(benefit));
  }, [selectedStep, benefit, validateStep]);

  const programByPoint =
    localStorage.getItem("cashbackProgramType") === "POINT";

  const allowsUseByEnvironment = Environment.id === 52 || Environment.id === 34;

  const getByTitle = (type) => {
    switch (type) {
      case "CASHBACK":
        return programByPoint ? "Pontos" : "Cashback";
      case "CASH_DISCOUNT":
        return "Desconto em R$";
      case "PERCENTAGE_DISCOUNT":
        return "Desconto em %";
      case "PRODUCT_DISCOUNT":
        return "Desconto em % nos produtos selecionados";
      case "PRODUCT_CASH_DISCOUNT":
        return "Desconto em R$ nos produtos selecionados";
      case "FREE_DELIVERY":
        return "Entrega grátis";
      default:
        return "";
    }
  };

  const getByValue = (type, value) => {
    switch (type) {
      case "CASHBACK":
        return programByPoint ? `${value} pontos para cada R$100` : `${value}%`;
      case "PERCENTAGE_DISCOUNT":
      case "PRODUCT_DISCOUNT":
        return `${value}%`;
      case "CASH_DISCOUNT":
      case "PRODUCT_CASH_DISCOUNT":
        return Currency.formatCurrency(value);
      case "FREE_DELIVERY":
        return null;
      default:
        return "";
    }
  };

  return (
    <Drawer
      visible={visible}
      onClose={() => {
        onClose();
        setSelectedStep(steps[0].key);
        setShowQrCode(false);
        initialized.current = false;
      }}
      withoutTransition
      direction="full-center"
      withoutScroll
      showCloseButton={false}
    >
      <div className={styles.container}>
        <If cond={selectedStep !== "TYPE" && !loading && !showQrCode}>
          <div className={styles.sidebar}>
            <ul>
              {steps
                .filter(({ show }) => show)
                .map((step) => {
                  const isSelected = selectedStep === step.key;
                  const isCompleted = completedSteps.includes(step.key);

                  return (
                    <li
                      key={String(step.key)}
                      className={css({
                        selected:
                          isSelected ||
                          (step.key === "AVAILABILITY_USE" &&
                            [
                              "USE",
                              "CHANNELS",
                              "DAYS",
                              "BRANDS",
                              "UNITS",
                              "DISCLOSURE",
                            ].includes(selectedStep)),
                      })}
                      onClick={(e) => {
                        e.stopPropagation();

                        if (isCompleted) {
                          if (step.key === "AVAILABILITY_USE")
                            return setSelectedStep("USE");
                          setSelectedStep(step.key);
                        }

                        // setSelectedStep(step.key);
                      }}
                    >
                      <div className="d-flex align-center h-100 gap-10">
                        {isCompleted ? <MiniCheck /> : <MiniInfo />}
                        <span>{step.label}</span>
                      </div>
                      <If cond={(step.items || []).length > 0}>
                        <ul>
                          {(step.items || []).map((step) => {
                            const isSelected = selectedStep === step.key;
                            const isCompleted = completedSteps.includes(
                              step.key
                            );

                            return (
                              <li
                                key={step.label}
                                className={css({ selected: isSelected })}
                                onClick={(e) => {
                                  e.stopPropagation();

                                  if (isCompleted) setSelectedStep(step.key);
                                  // setSelectedStep(step.key);
                                }}
                              >
                                {isCompleted ? <MiniCheck /> : <MiniInfo />}
                                <span>{step.label}</span>
                              </li>
                            );
                          })}
                        </ul>
                      </If>
                    </li>
                  );
                })}
            </ul>
          </div>
        </If>
        <div className={styles.homeHeader}>
          <div className="d-flex flex-column">
            <p className={styles.title}>
              {benefitToEdit
                ? showQrCode
                  ? "Alterações salvas com sucesso"
                  : "Editar cupom"
                : showQrCode
                ? "Cupom criado com sucesso"
                : "Criar cupom"}
            </p>
          </div>
          <div
            className={styles.close}
            onClick={() => {
              onClose();
              initialized.current = false;
            }}
          >
            <i className="fas fa-times" />
          </div>
        </div>

        <If cond={loading}>
          <div className="d-flex align-center justify-center h-100 w-100">
            <Loading />
          </div>
        </If>

        <If cond={showQrCode}>
          <BenefitShowQrCodeStep benefit={benefit?.code ? benefit : null} />
        </If>

        <If cond={!showQrCode && !loading}>
          <div
            className={css({
              content: true,
              hasSidebar: selectedStep !== "TYPE",
            })}
          >
            <If cond={!loading && !showQrCode}>
              <If cond={selectedStep === "TYPE"}>
                <BenefitSelectTypeStep
                  benefit={benefit}
                  onChange={(b) => onChangeBenefit(b)}
                  programByPoint={programByPoint}
                />
              </If>
              <If cond={selectedStep === "INFOS"}>
                <If cond={benefitToEdit}>
                  <div className={styles.benefitToEdit}>
                    <div className="d-flex align-center gap-15">
                      <div className={styles.icon}>
                        <i className="fal fa-gift" />
                      </div>
                      <div className="d-flex flex-column">
                        <p className={styles.title}>
                          {getByTitle(benefitToEdit?.type)}
                        </p>
                        <p className={styles.code}>{benefitToEdit?.code}</p>
                      </div>
                    </div>

                    <div className="d-flex align-center gap-10">
                      <If cond={benefitToEdit?.type !== "FREE_DELIVERY"}>
                        <p className={styles.value}>
                          {getByValue(
                            benefitToEdit?.type,
                            benefitToEdit?.value
                          )}
                        </p>
                      </If>
                      <p className={styles.value}>
                        {benefitToEdit?.expiration_date
                          ? `Expira em ${dayjs(
                              benefitToEdit?.expiration_date
                            ).format("DD/MM/YYYY")}`
                          : benefitToEdit?.expiration_days
                          ? `Expira em ${benefitToEdit?.expiration_days} ${
                              benefitToEdit?.expiration_days > 1
                                ? "dias"
                                : "dia"
                            }`
                          : "-"}
                      </p>
                    </div>
                  </div>
                </If>
                <BenefitInsertCodeStep
                  benefit={benefit}
                  partners={partners}
                  onChange={(b) => onChangeBenefit(b)}
                  programByPoint={programByPoint}
                  benefitToEdit={benefitToEdit}
                />
              </If>
              <If cond={selectedStep === "ADD"}>
                <div className={styles.box}>
                  <Label className="mb-5">Adição manual na carteira</Label>
                  <p className={styles.subtitle}>
                    Deseja permitir que o cliente possa incluir este benefício
                    em sua carteira? Você não poderá desfazer esta ação após a
                    criação do benefício.
                  </p>
                  <div className={styles.options}>
                    <div
                      className={css({
                        option: true,
                        selected: benefit.grant_type === "SYSTEM",
                      })}
                      onClick={() =>
                        setBenefit({ ...benefit, grant_type: "SYSTEM" })
                      }
                    >
                      <div className={styles.icon}>
                        <i className="fal fa-check" />
                      </div>
                      <p>Permitir</p>
                    </div>
                    <div
                      className={css({
                        option: true,
                        selected: benefit.grant_type === "MANUAL",
                      })}
                      onClick={() =>
                        setBenefit({ ...benefit, grant_type: "MANUAL" })
                      }
                    >
                      <div className={styles.icon}>
                        <i className="fal fa-ban" />
                      </div>
                      <p>Não permitir</p>
                    </div>
                  </div>
                </div>
              </If>
              <If
                cond={
                  selectedStep === "FIDELITY" &&
                  (GodMode.isGodMode || programByPoint)
                }
              >
                <div className={styles.box}>
                  <Label text="Cupom de fidelidade" />
                  <Hairline className="mt-15 mb-15" />
                  <Toggle2
                    reverse
                    checked={benefit.sellable}
                    onChange={(v) =>
                      setBenefit({ ...benefit, sellable: !benefit.sellable })
                    }
                    title="Este cupom pode ser comprado por pontos"
                  />
                  <If cond={benefit.sellable}>
                    <BaseCurrencyInput
                      design="two"
                      suffix={` ${benefit.price === 1 ? "ponto" : "pontos"}`}
                      prefix={""}
                      precision={0}
                      className="w-100 mb-0"
                      value={benefit.price}
                      onChange={(v) => setBenefit({ ...benefit, price: v })}
                    />
                  </If>
                </div>
              </If>
              <If cond={selectedStep === "USE"}>
                <If cond={!benefitToEdit}>
                  <div className={styles.box}>
                    <Label text="Determine o tipo de cupom" />
                    <Hairline className="mt-15 mb-15" />
                    <If cond={!benefit?.template}>
                      <Radio
                        text="Uso único"
                        family="use"
                        className="mb-10"
                        value={1}
                        checked={benefit.single_use}
                        onChange={() =>
                          setBenefit({ ...benefit, single_use: 1 })
                        }
                      />
                      <Radio
                        text="Uso múltiplo"
                        family="use"
                        className="mb-10"
                        value={0}
                        checked={benefit.single_use}
                        onChange={() =>
                          setBenefit({ ...benefit, single_use: 0 })
                        }
                      />
                      <Radio
                        text="Uso contínuo"
                        family="use"
                        value={2}
                        checked={benefit.single_use}
                        onChange={() =>
                          setBenefit({ ...benefit, single_use: 2 })
                        }
                      />

                      {allowsUseByEnvironment && (
                        <Radio
                          text="Uso único no ambiente"
                          family="use"
                          className="mt-10"
                          value={4}
                          checked={benefit.single_use}
                          disabled={true}
                          onChange={() =>
                            setBenefit({ ...benefit, single_use: 4 })
                          }
                        />
                      )}
                    </If>
                  </div>

                  <If cond={benefit.single_use === 0}>
                    <div className={styles.box}>
                      <Label text="Quantidade máxima de adições" />
                      <Hairline className="mt-15 mb-15" />
                      <Radio
                        text="Não"
                        family="block_add"
                        value={false}
                        checked={blockAdd}
                        onChange={() => setBlockAdd(false)}
                      />
                      <Radio
                        text="Sim"
                        family="block_add"
                        className="mt-10"
                        value={true}
                        checked={blockAdd}
                        onChange={() => setBlockAdd(true)}
                      />
                      <If cond={!!blockAdd || benefit.max_adds}>
                        <BaseCurrencyInput
                          design="two"
                          label="Quantas vezes este cupom pode ser adicionado?"
                          prefix={""}
                          precision={0}
                          className="w-100 mb-0 mt-10"
                          value={benefit.max_adds}
                          onChange={(v) =>
                            setBenefit({ ...benefit, max_adds: v })
                          }
                        />
                      </If>
                    </div>
                  </If>
                </If>
              </If>
              <If cond={selectedStep === "CHANNELS"}>
                <div className={styles.box}>
                  <BenefitUseTypeStep
                    benefit={benefit}
                    onChange={(b) => onChangeBenefit(b)}
                  />
                </div>
              </If>
              <If cond={selectedStep === "DAYS"}>
                <div className={styles.box}>
                  <BenefitVisibility
                    benefit={benefit}
                    onChange={(b) => onChangeBenefit(b)}
                  />
                </div>
              </If>
              <If cond={selectedStep === "BRANDS"}>
                <div className={styles.box}>
                  <BenefitEnvironment
                    benefit={benefit}
                    brands={brands}
                    onChange={(b) => onChangeBenefit(b)}
                  />
                </div>
              </If>
              <If cond={selectedStep === "UNITS"}>
                <div className={styles.box}>
                  <BenefitUnits
                    benefit={benefit}
                    units={units}
                    brands={brands}
                    onChange={(b) => onChangeBenefit(b)}
                  />
                </div>
              </If>
              <If cond={selectedStep === "CONTEMPLATE"}>
                <div className={styles.box}>
                  <Label>Gostaria de contemplar clientes?</Label>
                  <Hairline solid className="mt-15 mb-15" />
                  <Radio
                    family="contemplation"
                    className="mb-10"
                    value={false}
                    checked={benefit.contemplate}
                    onChange={() =>
                      onChangeBenefit({ ...benefit, contemplate: false })
                    }
                  >
                    Não
                  </Radio>
                  <Radio
                    family="contemplation"
                    value={true}
                    checked={benefit.contemplate}
                    onChange={() =>
                      onChangeBenefit({ ...benefit, contemplate: true })
                    }
                  >
                    Sim, gostaria
                  </Radio>

                  {!!benefit.contemplate && (
                    <div className="mt-10">
                      <ContemplateCustomers
                        embeddedComponent
                        onUpdateCustomerList={(customers) => {
                          onChangeBenefit({
                            ...benefit,
                            customersPhone: customers,
                          });
                        }}
                      />
                    </div>
                  )}
                </div>
              </If>
              <If cond={selectedStep === "DISCLOSURE"}>
                <If cond={GodMode.isGodMode}>
                  <div className={styles.box}>
                    <Label text="Cupom disponível para todos" />
                    <Hairline className="mt-15 mb-15" />
                    <Toggle2
                      reverse
                      checked={benefit.public}
                      onChange={(v) =>
                        setBenefit({ ...benefit, public: !benefit.public })
                      }
                      title="Sim, este cupom deve estar visível para todos os usuários"
                    />
                  </div>
                </If>
              </If>
            </If>
          </div>

          <div className={styles.footer}>
            <If cond={selectedStep !== "DISCLOSURE"}>
              <Button
                design="primary"
                disabled={!isValid}
                onClick={nextStep}
                className="w-100"
              >
                Próximo
              </Button>
            </If>
            <If cond={selectedStep === "DISCLOSURE"}>
              <Button
                design="primary"
                disabled={!isValid}
                onClick={saveCoupon}
                className="w-100"
              >
                Concluir {benefitToEdit ? "edição" : "criação"} de cupom
              </Button>
            </If>
          </div>
        </If>
      </div>
    </Drawer>
  );
};

const benefitTypes = {
  CASH_DISCOUNT: "CASH_DISCOUNT",
  PERCENTAGE_DISCOUNT: "PERCENTAGE_DISCOUNT",
  FREE_DELIVERY: "FREE_DELIVERY",
  CASHBACK: "CASHBACK",
  PRODUCT_DISCOUNT: "PRODUCT_DISCOUNT",
  PRODUCT_CASH_DISCOUNT: "PRODUCT_CASH_DISCOUNT",
};
